import type { BrandKit } from '../brand.types'
import logo from '@/assets/brands/lowcountry-male/logo.svg'
import logoReverse from '@/assets/brands/lowcountry-male/logo-reverse.svg'
import favicon from '@/assets/brands/lowcountry-male/favicon.svg'

/**
 * Lowcountry Male — Brand Guide 2024.
 *
 * Palette (guide, "Color Palette"):
 *   Slate Blue  #5B7B8D  primary brand colour — headlines, logo
 *   Orange      #F17821  primary accent — CTAs, buttons, highlights
 *   Navy        #4A6572  body text, secondary elements
 *   Light Blue  #B5C7D3  backgrounds, accents, icon fills
 *
 * Orange is mapped to `cta` rather than `primary` on purpose: the guide
 * scopes it to calls-to-action (~20% of the palette), so turning every
 * primary-coloured surface in the app orange would misapply the brand.
 *
 * `#3F5763` / `#D6631A` are darker steps derived for hover/active states —
 * interaction states only, never used to render the logo.
 */
export const lowcountryMaleBrand: BrandKit = {
  id: 'lowcountry-male',
  name: 'Lowcountry Male',
  coBrand: 'aqua-vitae',

  logo,
  logoOnBrand: logoReverse,
  favicon,

  palette: {
    primary: {
      main: '#5B7B8D',
      dark: '#4A6572',
      light: '#B5C7D3',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#4A6572',
      dark: '#3F5763',
      light: '#B5C7D3',
      contrastText: '#FFFFFF',
    },
    cta: {
      main: '#F17821',
      dark: '#D6631A',
      contrastText: '#FFFFFF',
    },
    // Guide, Typography: body text Navy, small text Medium Gray. The guide
    // names "Medium Gray" without a hex, so the platform neutral is kept.
    text: {
      primary: '#4A6572',
      secondary: '#64748B',
    },
    // Guide, Secondary Colors: Light Blue-Gray is for "icon fills".
    iconFill: '#B5C7D3',
    // Links keep the platform blue they had before branding, by request.
    link: '#088596',
  },

  /**
   * Guide, Typography: "Sans-serif family (Helvetica, Arial, or Open Sans)".
   * Sizes are the low end of each of the guide's ranges, converted from pt
   * at 1pt = 4/3px:
   *   Headline/H1    32px (set by request; the guide's range is 36-48pt)
   *   Subheadline/H2 24pt → 32px, semi-bold
   *   Body Text      16pt → 21px, regular
   *   Small Text     14px (set by request; the guide's 12pt converts to 16px)
   */
  typography: {
    fontFamily: '"Open Sans", "Helvetica", "Arial", sans-serif',
    // Variable axis (not fixed instances) so the non-standard weights below
    // render exactly rather than snapping to the nearest 100.
    googleFont: 'Open+Sans:wght@300..800',
    scale: { headline: 32, subheadline: 32, body: 21, bodySmall: 16, small: 14 },
    // Guide weights (700 / 600 / 400) raised 20% on request.
    // 700 x 1.2 = 840, above Open Sans' 800 ceiling, so the headline caps
    // at 800 — a 14% increase rather than 20%.
    weights: { headline: 800, subheadline: 720, body: 480 },
  },

  // Guide, Effective Color Combinations: "Professional: Slate Blue with
  // White". Navy -> Slate Blue, both from the primary palette.
  brandSurface: 'linear-gradient(135deg, #4A6572 0%, #5B7B8D 100%)',

  // Taglines and value propositions from the guide's Messaging Framework.
  bannerCopy: {
    clinician: {
      title: ['Concierge  Health Care'],
      subtitle:
        "Manage your patients, treatment plans and telehealth visits in one place — built for the leading men's health clinic in the Lowcountry.",
      features: [
        'Multi-clinic provider management',
        'PCI-compliant payment processing',
        'Integrated telehealth & encounters',
      ],
    },
    patient: {
      title: ['Reclaim Your Vitality'],
      subtitle:
        'Book appointments, follow your treatment plan and stay connected with your care team — discreetly and on your schedule.',
      features: [
        'Book & manage your appointments',
        'View your treatment history',
        'Access your invoices & receipts',
      ],
    },
  },

  // Supplements are sold direct through Lowcountry Male's own shops, not
  // through the Fullscript dispensary. `amazon` is pending the store URL
  // from the client — until it is set the Purchase menu shows Shopify only.
  storefronts: {
    shopify: 'https://lowcountrymale.myshopify.com/',
  },
}
